import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { without } from 'lodash';

import useRg from 'Admin/Clusters/CreateClusterDialog/useVpcs/useRg';

import { RouteServersQuery } from './queries';

// resource group is represented as a string ex. __wiping__
type ResourceGroup = string;

/**
 * During the creation of cluster network a user can opt in to create a new resource group,
 * it needs to be available for selection in following steps.
 */
export function computeResourceGroups(resourceGroups: ResourceGroup[], resourceGroup?: ResourceGroup): ResourceGroup[] {
  // resource group is not defined, return resourceGroups
  // we got from BE
  if (!resourceGroup) {
    return resourceGroups;
  }

  // resourceGroup already exists, shift it to the start
  if (resourceGroups.includes(resourceGroup)) {
    return [resourceGroup, ...without(resourceGroups, resourceGroup)];
  }

  // resourceGroup is newly created, prepend it to list
  // we got from BE
  return [resourceGroup, ...resourceGroups];
}

export type UseRouteServerPickerInput = {
  datacenterId?: string;
  subscriptionId?: string;
  cloudServiceId?: string;
  resourceGroup?: string;
  newResourceGroup?: string;
  skip?: boolean;
};

export enum RoutePickerMode {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
}

export function useRouteServer({
  subscriptionId,
  cloudServiceId,
  datacenterId,
  resourceGroup,
  newResourceGroup,
  skip,
}: UseRouteServerPickerInput) {
  const { resourceGroups, loading, refetch, error } = useRg({
    variables: {
      cloudServiceId: cloudServiceId!,
      datacenterId: datacenterId!,
      subscriptionId,
    },
    skip,
  });

  const err = useMemo(() => {
    if (!error?.graphQLErrors) {
      return '';
    }

    const e = error.graphQLErrors[0];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e as any)?.details ?? '';
  }, [error]);

  const { data: { azureRouteServers = [] } = {}, loading: azureRouteServerLoading } = useQuery(RouteServersQuery, {
    variables: {
      subscriptionId,
      cloudServiceId,
      datacenterId,
      resourceGroup,
    },
    skip: !cloudServiceId || !resourceGroup,
  });

  const hasRouteServer = Boolean(azureRouteServers.length);

  return {
    resourceGroups: computeResourceGroups(resourceGroups, newResourceGroup),
    azureRouteServerLoading,
    azureRouteServers,
    loading,
    error: err,
    hasRouteServer,
    handleRefresh: refetch,
  };
}
